import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

import type { GrantRequest, GrantStat, Stat, Verification } from '../../types/types';

// Query response types
export interface GetStatsResponse { getStats: Stat[] }
export interface GetGrantStatsResponse { getGrantStats: GrantStat[] }
export interface GetGrantRequestsResponse { getGrantRequests: GrantRequest[] }
export interface GetVerificationResponse { getVerification: Verification }

const statsQuery = gql`
    query getStats(
        $UserName: String!) {
        getStats(UserName: $UserName) {
            StatId
            StatName
            StatDate
            StatValue
        }
    }`;

function useStatsQuery(userName: string) {
    return useQuery<GetStatsResponse>(
        statsQuery,
        {
            variables: {
                UserName: userName,
            },
        },
    );
}

const grantStatsQuery = gql`
    query getGrantStats(
        $UserName: String!,
        $UserOrOrg: String!) {
        getGrantStats(UserName: $UserName, UserOrOrg: $UserOrOrg) {
            UserName
            ReportDate
            TimePeriod
            NumberOfAwardsGranted
            ByAwardName
            ByDate
            ByRecipient
            ByGrantor
        }
    }
`;

// We have overloaded userName to support OrganizationIds as well
function useGrantStatsQuery(userName: string, statType: string) {
    const UserOrOrg = statType ?? 'user';
    return useQuery<GetGrantStatsResponse>(
        grantStatsQuery,
        {
            variables: {
                UserName: userName,
                UserOrOrg,
            },
        },
    );
}

const grantRequestsQuery = gql`
    query getGrantRequests(
        $UserName: String!) {
        getGrantRequests(UserName: $UserName) {
            UserName
            OrganizationId
            GivenName
            FamilyName
            Title
            Email
            Phone
            LinkedIn
            Completed
            CreatedDate
            RequestId
            Referrer
        }
    }
`;

function useGrantRequestsQuery(userName: string) {
    return useQuery<GetGrantRequestsResponse>(
        grantRequestsQuery,
        {
            variables: {
                UserName: userName,
            },
        },
    );
}

const verificationQuery = gql`
    query getVerification(
        $UserName: String!,
        $ResourceName: String!,
        $VerificationCode: String!) {
        getVerification(UserName: $UserName, ResourceName: $ResourceName, VerificationCode: $VerificationCode) {
            UserName
            ResourceName
            VerificationCode
            VerifiedDate
        }
    }
`;

function useVerificationQuery(userName: string, resourceName: string, verificationCode: string) {
    return useQuery<GetVerificationResponse>(
        verificationQuery,
        {
            variables: {
                UserName: userName,
                ResourceName: resourceName,
                VerificationCode: verificationCode,
            },
        },
    );
}

const createConsumerProductInterestMutation = gql`
    mutation createConsumerProductInterest(
        $Email: String!,
        $Comment: String,
    ) {
        createConsumerProductInterest(
            Email: $Email,
            Comment: $Comment,
        ) {
            Email
            Comment
        }
    }
`;

const createGrantRequestMutation = gql`
    mutation createGrantRequest(
        $UserName: String!,
        $RequestId: String!,
        $OrganizationId: String!,
        $GivenName: String!,
        $FamilyName: String!,
        $Phone: String!,
        $Email: String!,
        $Title: String!,
        $LinkedIn: String,
        $Completed: Boolean!,
        $CreatedDate: String!,
        $Referrer: String!,
    ) {
        createGrantRequest(
            UserName: $UserName,
            RequestId: $RequestId,
            OrganizationId: $OrganizationId,
            GivenName: $GivenName,
            FamilyName: $FamilyName,
            Phone: $Phone,
            Email: $Email,
            Title: $Title,
            LinkedIn: $LinkedIn,
            Completed: $Completed,
            CreatedDate: $CreatedDate,
            Referrer: $Referrer,
        ) {
            UserName
            RequestId
            OrganizationId
            GivenName
            FamilyName
            Phone
            Email
            Title
            LinkedIn
            Completed
            CreatedDate
            Referrer
        }
    }
`;

const markGrantRequestCompleted = gql`
    mutation markGrantRequestCompleted($RequestId: String!, $UserName: String!) {
        markGrantRequestCompleted(RequestId: $RequestId, UserName: $UserName) {
            UserName
            RequestId
            Completed
        }
    }
`;

const addNomination = gql`
    mutation addNomination(
        $NominationId: String!,
        $NomineeEmail: String!,
        $MinterEmail: String!,
        $Reason: String!,
        $NominatorEmail: String!) {
        addNomination(
            NominationId: $NominationId,
            NomineeEmail: $NomineeEmail,
            MinterEmail: $MinterEmail,
            Reason: $Reason,
            NominatorEmail: $NominatorEmail,
        ) {
            NominationId
            NomineeEmail
            MinterEmail
            Reason
            NominatorEmail
        }
    }
`;

export {
    statsQuery,
    grantStatsQuery,
    grantRequestsQuery,
    verificationQuery,
    createConsumerProductInterestMutation,
    createGrantRequestMutation,
    markGrantRequestCompleted,
    addNomination,
    useStatsQuery,
    useGrantStatsQuery,
    useGrantRequestsQuery,
    useVerificationQuery,
};
